// GSTManagementBusiness.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../api/axiosInstance";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import LoadingSpinner from "./LoadingSpinner";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";

function GSTManagementBusiness() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [hsnList, setHsnList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentHSN, setCurrentHSN] = useState({
    hsn_id: "",
    hsn_code: "",
    gst_percentage: "",
    description: "",
    category: ""
  });
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const business_id = localStorage.getItem('business_id');

  useEffect(() => {
    fetchHSNCodes();
  }, [searchText]);

  const fetchHSNCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        '/hsn/list',
        { params: { search_text: searchText, business_id } }
      );
      if (response.data.status === "success") {
        setHsnList(response.data.result.list || []);
      }
    } catch (error) {
      console.error("Error fetching HSN codes:", error);
      toast.error("Failed to load HSN codes");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setCurrentHSN({
      hsn_id: "",
      hsn_code: "",
      gst_percentage: "",
      description: "",
      category: ""
    });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (hsn) => {
    // Only allow editing if this HSN belongs to the business
    if (hsn.business_id !== business_id) {
      toast.error("You can only edit HSN codes created by your business");
      return;
    }

    setEditMode(true);
    setCurrentHSN({
      hsn_id: hsn._id,
      hsn_code: hsn.hsn_code,
      gst_percentage: hsn.gst_percentage,
      description: hsn.description || "",
      category: hsn.category || ""
    });
    setErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!currentHSN.hsn_code.trim()) {
      newErrors.hsn_code = "HSN Code is required";
    }
    if (!currentHSN.gst_percentage || currentHSN.gst_percentage < 0) {
      newErrors.gst_percentage = "Valid GST percentage is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const apiUrl = editMode ? '/hsn/update' : '/hsn/create';

      const payload = {
        hsn_code: currentHSN.hsn_code,
        gst_percentage: parseFloat(currentHSN.gst_percentage),
        description: currentHSN.description,
        category: currentHSN.category,
        business_id: business_id
      };

      if (editMode) {
        payload.hsn_id = currentHSN.hsn_id;
      }

      const response = await api.post(apiUrl, payload);

      if (response.data.status === "success") {
        toast.success(
          editMode ? "HSN Code updated successfully!" : "HSN Code added successfully!"
        );
        setShowModal(false);
        fetchHSNCodes();
      }
    } catch (error) {
      console.error("Error saving HSN Code:", error);
      toast.error(error.response?.data?.message || "Failed to save HSN Code");
    }
  };

  const handleDelete = async (hsn) => {
    // Only allow deleting if this HSN belongs to the business
    if (hsn.business_id !== business_id) {
      toast.error("You can only delete HSN codes created by your business");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this HSN Code?")) {
      return;
    }

    try {
      const response = await api.post(
        '/hsn/delete',
        { hsn_id: hsn._id }
      );

      if (response.data.status === "success") {
        toast.success("HSN Code deleted successfully!");
        fetchHSNCodes();
      }
    } catch (error) {
      console.error("Error deleting HSN Code:", error);
      toast.error("Failed to delete HSN Code");
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading GST/HSN codes..." />;
  }

  return (
    <div className="wrapper">
      <NavigationBar />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">GST Management</h1>
              </div>
              <div className="col-sm-6">
                <button
                  className="btn btn-primary float-right"
                  onClick={handleAdd}
                >
                  <i className="fas fa-plus mr-2"></i>Add HSN Code
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search HSN Code, Description, or Category..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="card-body table-responsive p-0">
                <table className="table table-hover text-nowrap">
                  <thead>
                    <tr>
                      <th>HSN Code</th>
                      <th>GST %</th>
                      <th>CGST %</th>
                      <th>SGST %</th>
                      <th>IGST %</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Source</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hsnList.length > 0 ? (
                      hsnList.map((hsn) => (
                        <tr key={hsn._id}>
                          <td>{hsn.hsn_code}</td>
                          <td>{hsn.gst_percentage}%</td>
                          <td>{hsn.cgst}%</td>
                          <td>{hsn.sgst}%</td>
                          <td>{hsn.igst}%</td>
                          <td>{hsn.description}</td>
                          <td>{hsn.category}</td>
                          <td>
                            {hsn.business_id === business_id ? (
                              <span className="badge badge-primary">Your Business</span>
                            ) : (
                              <span className="badge badge-secondary">Global</span>
                            )}
                          </td>
                          <td>
                            {hsn.business_id === business_id ? (
                              <>
                                <button
                                  className="btn btn-sm btn-info mr-2"
                                  onClick={() => handleEdit(hsn)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(hsn)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </>
                            ) : (
                              <span className="text-muted">Read Only</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No HSN Codes found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editMode ? "Edit HSN Code" : "Add HSN Code"}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>HSN Code<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-control ${errors.hsn_code ? "is-invalid" : ""}`}
                      value={currentHSN.hsn_code}
                      onChange={(e) =>
                        setCurrentHSN({ ...currentHSN, hsn_code: e.target.value })
                      }
                      disabled={editMode}
                    />
                    {errors.hsn_code && (
                      <div className="invalid-feedback">{errors.hsn_code}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>GST Percentage<span className="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${errors.gst_percentage ? "is-invalid" : ""}`}
                      value={currentHSN.gst_percentage}
                      onChange={(e) =>
                        setCurrentHSN({ ...currentHSN, gst_percentage: e.target.value })
                      }
                    />
                    {errors.gst_percentage && (
                      <div className="invalid-feedback">{errors.gst_percentage}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={currentHSN.description}
                      onChange={(e) =>
                        setCurrentHSN({ ...currentHSN, description: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentHSN.category}
                      onChange={(e) =>
                        setCurrentHSN({ ...currentHSN, category: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? "Update" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default GSTManagementBusiness;